import React, {useCallback, useEffect, useRef, useState} from 'react';
import ModalContainer from '../../uiToolkit/ModalContainer';
import Button from '../../uiToolkit/Buttons/Button';
import styles from './RecordVideoModal.module.css';
import {lessonEditorText} from '../../pages/LessonEditor/lessonEditorText';

interface Props {
  onSave: (file: File) => void;
  onCancel: () => void;
}

type RecordingState = 'idle' | 'recording' | 'preview';

const getMimeType = () => {
  if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus'))
    return 'video/webm;codecs=vp8,opus';
  if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus'))
    return 'video/webm;codecs=vp9,opus';
  if (MediaRecorder.isTypeSupported('video/webm')) return 'video/webm';
  if (MediaRecorder.isTypeSupported('video/mp4')) return 'video/mp4';
  return '';
};

const RecordVideoModal: React.FC<Props> = ({onSave, onCancel}) => {
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [permissionError, setPermissionError] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const liveVideoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mimeTypeRef = useRef('');

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    initCamera();
    return () => {
      if (recorderRef.current?.state === 'recording') {
        recorderRef.current.stop();
      }
      stopStream();
      clearTimer();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, []);

  const initCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {width: {ideal: 1280}, height: {ideal: 720}},
        audio: true,
      });
      streamRef.current = stream;
      if (liveVideoRef.current) {
        liveVideoRef.current.srcObject = stream;
      }
    } catch {
      setPermissionError(true);
    }
  };

  const startRecording = () => {
    if (!streamRef.current) return;

    chunksRef.current = [];
    mimeTypeRef.current = getMimeType();

    const recorder = new MediaRecorder(streamRef.current, {
      mimeType: mimeTypeRef.current || undefined,
    });

    recorder.ondataavailable = e => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      clearTimer();
      const blobType = mimeTypeRef.current.includes('mp4')
        ? 'video/mp4'
        : mimeTypeRef.current || 'video/webm';
      const blob = new Blob(chunksRef.current, {type: blobType});
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setRecordingState('preview');
      stopStream();
    };

    recorderRef.current = recorder;
    recorder.start();
    setElapsedSeconds(0);
    setRecordingState('recording');

    timerRef.current = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
  };

  const reRecord = async () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setElapsedSeconds(0);
    setRecordingState('idle');
    await initCamera();
  };

  const saveRecording = () => {
    if (!chunksRef.current.length) return;
    const blobType = mimeTypeRef.current.includes('mp4')
      ? 'video/mp4'
      : mimeTypeRef.current || 'video/webm';
    const blob = new Blob(chunksRef.current, {type: blobType});
    const now = new Date();
    const day = now.getDate();
    const month = now.toLocaleString('en-US', {month: 'short'});
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const fileName = `Rec ${day} ${month} ${hours}:${minutes}.webm`;
    const file = new File([blob], fileName, {
      type: 'video/webm',
    });
    onSave(file);
  };

  const formatTime = (seconds: number) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, '0');
    const s = String(seconds % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  if (permissionError) {
    return (
      <ModalContainer onClose={onCancel}>
        <div className={styles.container}>
          <p className={styles.permissionError}>
            {lessonEditorText.cameraPermissionError}
          </p>
          <div className={styles.actions}>
            <Button
              title={lessonEditorText.closeRecording}
              onClick={onCancel}
              uiType="hollow"
            />
            <Button
              title={lessonEditorText.retryRecording}
              onClick={() => {
                setPermissionError(false);
                initCamera();
              }}
              uiType="action"
            />
          </div>
        </div>
      </ModalContainer>
    );
  }

  return (
    <ModalContainer onClose={onCancel}>
      <div className={styles.container}>
        <h3 className={styles.title}>{lessonEditorText.recordVideoTitle}</h3>

        <div className={styles.videoContainer}>
          {recordingState !== 'preview' && (
            <video ref={liveVideoRef} autoPlay muted playsInline />
          )}
          {recordingState === 'preview' && previewUrl && (
            <video key={previewUrl} controls playsInline>
              <source src={previewUrl} type="video/webm" />
            </video>
          )}
        </div>

        {recordingState !== 'preview' && (
          <div className={styles.controls}>
            <button
              className={`${styles.recordButton} ${
                recordingState === 'recording' ? styles.recording : ''
              }`}
              onClick={
                recordingState === 'recording' ? stopRecording : startRecording
              }>
              <span className={styles.recordInner} />
            </button>
            <span className={styles.timer}>{formatTime(elapsedSeconds)}</span>
          </div>
        )}

        {recordingState === 'preview' && (
          <div className={styles.actions}>
            <Button
              title={lessonEditorText.reRecordVideo}
              onClick={reRecord}
              uiType="hollow"
            />
            <Button
              title={lessonEditorText.saveAndProceed}
              onClick={saveRecording}
              uiType="action"
            />
          </div>
        )}
      </div>
    </ModalContainer>
  );
};

export default RecordVideoModal;
